/**
 * The print document model (03-modules.md §5 "Print service"): one
 * device-independent description of a printed page, rendered by the server from
 * templates (KOT, bill, test page, table QR) and turned into bytes by the device
 * bridge (ESC/POS for 58/80 mm receipt printers, M4) or into HTML for the dev
 * "print to PDF" bridge (packages/device-bridge).
 *
 * A document is a list of blocks printed top to bottom. Widths are in characters
 * of the printer's normal font (Font A: 32 per line on 58 mm paper, 48 on 80 mm,
 * `PAPER_CHARS`); a `size` of 2 doubles a character's width and/or height, so a
 * double-width line holds half as many characters. The model holds no bytes, no
 * code pages and no printer commands: the renderer chooses them (rupee sign
 * fallback, QR module size, logo raster).
 *
 * Kept small on purpose: text runs with bold/underline/invert/size/alignment,
 * columns (a row of cells: item, quantity, amount), separators, feeds, QR codes,
 * barcodes, a logo reference and the paper cut.
 */
import { z } from 'zod';

export const PAPER_WIDTHS_MM = [58, 80] as const;
export type PaperWidthMm = (typeof PAPER_WIDTHS_MM)[number];

/** Characters per line in the printer's normal font. */
export const PAPER_CHARS: Readonly<Record<PaperWidthMm, number>> = { 58: 32, 80: 48 };

export const PRINT_ALIGNS = ['left', 'center', 'right'] as const;
export type PrintAlign = (typeof PRINT_ALIGNS)[number];

/** 1 = normal, 2 = double (ESC/POS `GS !` multipliers). */
const scaleSchema = z.union([z.literal(1), z.literal(2)]);

const styleShape = {
  bold: z.boolean().optional(),
  underline: z.boolean().optional(),
  /** White on black. */
  invert: z.boolean().optional(),
  /** Character width multiplier. */
  width: scaleSchema.optional(),
  /** Character height multiplier. */
  height: scaleSchema.optional(),
};

const textValue = z.string().max(2_000);

/** One paragraph; wrapped at the line width by the renderer. */
export const printTextSchema = z.object({
  t: z.literal('text'),
  text: textValue,
  align: z.enum(PRINT_ALIGNS).optional(),
  ...styleShape,
});

/** One cell of a row. `flex` shares the line between cells (default 1). */
export const printCellSchema = z.object({
  text: z.string().max(500),
  flex: z.int().min(1).max(48).optional(),
  align: z.enum(PRINT_ALIGNS).optional(),
  bold: z.boolean().optional(),
});

/** Columns on one line (item | qty | amount); a cell too long wraps inside its column. */
export const printRowSchema = z.object({
  t: z.literal('row'),
  cells: z.array(printCellSchema).min(1).max(6),
  bold: z.boolean().optional(),
  width: scaleSchema.optional(),
  height: scaleSchema.optional(),
});

/** A full-width separator line. */
export const printRuleSchema = z.object({
  t: z.literal('rule'),
  char: z.enum(['-', '=', '*', '.']).optional(),
});

export const printFeedSchema = z.object({ t: z.literal('feed'), lines: z.int().min(1).max(10) });

export const printQrSchema = z.object({
  t: z.literal('qr'),
  data: z.string().min(1).max(1_000),
  /** Module size 1..8 (ESC/POS `GS ( k` size); default 6. */
  size: z.int().min(1).max(8).optional(),
  align: z.enum(PRINT_ALIGNS).optional(),
});

export const printBarcodeSchema = z.object({
  t: z.literal('barcode'),
  data: z.string().min(1).max(80),
  format: z.enum(['code128', 'ean13']),
  align: z.enum(PRINT_ALIGNS).optional(),
});

/** The outlet's logo (a stored raster the device caches); `ref` names it. */
export const printLogoSchema = z.object({
  t: z.literal('logo'),
  ref: z.string().min(1).max(200),
  align: z.enum(PRINT_ALIGNS).optional(),
});

export const printCutSchema = z.object({ t: z.literal('cut'), partial: z.boolean().optional() });

export const printBlockSchema = z.discriminatedUnion('t', [
  printTextSchema,
  printRowSchema,
  printRuleSchema,
  printFeedSchema,
  printQrSchema,
  printBarcodeSchema,
  printLogoSchema,
  printCutSchema,
]);
export type PrintBlock = z.output<typeof printBlockSchema>;
export type PrintCell = z.output<typeof printCellSchema>;

export const PRINT_DOC_VERSION = 1;

/** A printed page (`print_job.doc`). */
export const printDocSchema = z.object({
  version: z.literal(PRINT_DOC_VERSION),
  paperMm: z.union([z.literal(58), z.literal(80)]),
  /** What it is, for logs and the dev bridge's window title (never printed). */
  title: z.string().max(120),
  /**
   * Printed large across the top and bottom of a copy that is not the original
   * (`DUPLICATE` on a reprinted KOT, `REPRINT` on a reprinted bill).
   */
  watermark: z.string().max(20).nullable(),
  /** Copies the device prints of this document (station_printer.copies). */
  copies: z.int().min(1).max(5),
  blocks: z.array(printBlockSchema).max(2_000),
});
export type PrintDoc = z.output<typeof printDocSchema>;

/** Watermarks (03-modules.md §5: a reprinted KOT is marked DUPLICATE). */
export const KOT_REPRINT_WATERMARK = 'DUPLICATE';
export const BILL_REPRINT_WATERMARK = 'REPRINT';
